/** Join logical tooltip paragraphs — rendered as separate blocks in the UI. */
export function dataTipParagraphs(...paragraphs: string[]): string {
  return paragraphs.map((part) => part.trim()).filter(Boolean).join("\n");
}

export function escapeDataTipHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Split semicolon-separated clauses without breaking nested parentheses. */
function splitSemicolonClauses(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    else if (ch === ";" && depth === 0) {
      const piece = text.slice(start, i).trim();
      if (piece) out.push(piece);
      start = i + 1;
    }
  }
  const tail = text.slice(start).trim();
  if (tail) out.push(tail);
  return out;
}

/**
 * Format paper-agent justification for the Why modal.
 * Avoids sentence splitting that breaks on "Ann.", dates (7/28.), etc.
 */
export function formatAgentJustificationHtml(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  const cursorSplit = trimmed.split(/\sCursor:\s/);
  const mainRaw = (cursorSplit[0] ?? "").trim().replace(/\.$/, "");
  const tailRaw = cursorSplit.slice(1).join(" Cursor: ").trim();

  let cursorBody = tailRaw;
  let unsuitable = "";
  const unsuitableMatch = tailRaw.match(/\sUnsuitable if:\s/i);
  if (unsuitableMatch && unsuitableMatch.index != null) {
    cursorBody = tailRaw.slice(0, unsuitableMatch.index).trim();
    unsuitable = tailRaw
      .slice(unsuitableMatch.index)
      .replace(/^\s*Unsuitable if:\s/i, "")
      .trim();
  }

  const sections: string[] = [];
  const mainItems = splitSemicolonClauses(mainRaw);
  if (mainItems.length) {
    sections.push(`
      <section class="sfe-agent-why-section">
        <h4 class="sfe-agent-why-section-title">Selection</h4>
        <ul class="sfe-agent-why-list">
          ${mainItems.map((item) => `<li>${escapeDataTipHtml(item)}</li>`).join("")}
        </ul>
      </section>`);
  }

  if (cursorBody) {
    sections.push(`
      <section class="sfe-agent-why-section">
        <h4 class="sfe-agent-why-section-title">Cursor</h4>
        <p class="sfe-agent-why-prose">${escapeDataTipHtml(cursorBody)}</p>
      </section>`);
  }

  if (unsuitable) {
    sections.push(`
      <section class="sfe-agent-why-section">
        <h4 class="sfe-agent-why-section-title">Unsuitable if</h4>
        <p class="sfe-agent-why-prose">${escapeDataTipHtml(unsuitable)}</p>
      </section>`);
  }

  if (!sections.length) {
    return `<p class="sfe-agent-why-prose">${escapeDataTipHtml(trimmed)}</p>`;
  }

  return `<div class="sfe-agent-why">${sections.join("")}</div>`;
}

/** Split tip text into digestible HTML paragraphs (escaped). */
export function formatDataTipHtml(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  let parts = trimmed
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 1 && trimmed.length > 100) {
    const sentences = trimmed.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g);
    if (sentences && sentences.length >= 3) {
      parts = sentences.map((sentence) => sentence.trim()).filter(Boolean);
    }
  }

  if (parts.length <= 1) return escapeDataTipHtml(trimmed);

  return parts
    .map((part) => `<p class="sfe-data-tip-p">${escapeDataTipHtml(part)}</p>`)
    .join("");
}
